import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Input, Modal, Select, Table, Tag, Tooltip, message, theme } from 'antd';
import { ArrowLeftOutlined, ExportOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { OPERATION, ROUTES } from '@/constants';
import { rememberTeamBoard, teamBoardState } from '@/components/team-assignments/teamBoardNav';
import { useTranslation } from 'react-i18next';
import AssignmentChip from '@/components/team-assignments/AssignmentChip';
import TeamFirmCard from '@/components/team-assignments/TeamFirmCard';
import {
  CLIENT_STATUSES,
  FUNCTION_BAR,
  FUNCTIONS,
  QUICK_FILTERS,
  absenceOf,
  personHeadcountIn,
  personName,
  personStats,
} from '@/components/team-assignments/model';
import { cancelTeamSubstitution } from '@/services/teamAssignments.service';
import BulkTransferDialog from './BulkTransferDialog';
import GeneralTasks from './GeneralTasks';
import SubstitutionDialog from './SubstitutionDialog';

// Below this width the view flows like a normal page.
const DESKTOP_MIN = 1024;
// Bottom breathing room under the board, in px.
const BOTTOM_GAP = 16;
// Below this share of the page's scroll frame the fitted table is only a small
// window under its controls, so the view flows like a normal page instead.
const MIN_FIT_SHARE = 0.65;
const FRAME_SELECTOR = '.with-y-overflow';
const ALL_ROW = '__all';

/**
 * Fits the board to the page's scroll frame: the box starts where it is drawn
 * and ends at the bottom of that frame (not of the window, which would leave
 * the frame scrolling as well); the table body gets whatever height is left
 * under its own header. Measured live, so it follows zoom, resizing and the
 * controls wrapping to more lines.
 *
 * When the controls above the table leave it too little of the frame (a small
 * screen, one person's card, open tasks or filters), nothing is fitted: the
 * frame scrolls the controls away and the table has the whole frame.
 */
const useFitToWindow = (deps) => {
  const boxRef = useRef(null);
  const tableWrapRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(null);
  const [bodyHeight, setBodyHeight] = useState(null);

  useEffect(() => {
    let ro = null;
    const measure = () => {
      const box = boxRef.current;
      const wrap = tableWrapRef.current;
      const frame = box?.closest(FRAME_SELECTOR);
      const frameH = frame ? frame.clientHeight : window.innerHeight;
      // Measured from the top of the frame's content, so scrolling it never changes the outcome.
      const fromFrameTop = (el) => el.getBoundingClientRect().top - (frame ? frame.getBoundingClientRect().top - frame.scrollTop : 0);
      if (!box || !wrap || window.innerWidth < DESKTOP_MIN || frameH - fromFrameTop(wrap) - BOTTOM_GAP < frameH * MIN_FIT_SHARE) {
        setBoxHeight(null);
        setBodyHeight(null);
        return;
      }
      const h = Math.max(320, Math.floor(frameH - fromFrameTop(box) - BOTTOM_GAP));
      setBoxHeight(h);
      const head = wrap.querySelector('.ant-table-header, .ant-table-thead');
      // The header wraps to two lines on narrower screens only after antd
      // lays the columns out; without watching it the body keeps the old
      // height and the last row ends up under the frame (Bora 19.10).
      if (head && ro) ro.observe(head);
      const headH = head ? head.getBoundingClientRect().height : 40;
      const room = wrap.getBoundingClientRect().height;
      if (room) setBodyHeight(Math.max(160, room - headH - 2));
    };
    ro = new ResizeObserver(measure);
    measure();
    if (boxRef.current) ro.observe(boxRef.current);
    if (tableWrapRef.current) ro.observe(tableWrapRef.current);
    const frame = boxRef.current?.closest(FRAME_SELECTOR);
    if (frame) ro.observe(frame);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return { boxRef, tableWrapRef, boxHeight, bodyHeight };
};

/** Pill buttons for one choice, with a count each. */
export function PillGroup({ items, value, onChange, label }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <Button
          key={it.key}
          shape="round"
          size="small"
          type={value === it.key ? 'primary' : 'default'}
          aria-pressed={value === it.key}
          onClick={() => onChange(it.key)}
        >
          {it.label}
          {it.count !== undefined && (
            <b className={`ml-1 tabular-nums ${value !== it.key && it.warn && it.count ? 'text-orange-600' : ''}`}>{it.count}</b>
          )}
        </Button>
      ))}
    </div>
  );
}

/**
 * The combined view, in three steps on the full width:
 * - the team: one row per person with what they carry (managers start here);
 * - one person: only the clients that person works on, and what they do there;
 * - all clients: one row per legal entity with everybody on it (search, quick
 *   filters, or "All companies" lead here).
 * A client row opens its card in place. Quick filters and the client status
 * stay folded behind the "Filters" button until someone needs them.
 */
export default function CombinedView({
  model,
  isManager,
  me,
  fn,
  setFn,
  person,
  setPerson,
  list,
  setList,
  goOverview,
  quick,
  setQuick,
  q,
  setQ,
  focusCompany,
  onFocused,
  clientStatus,
  setClientStatus,
}) {
  const { t } = useTranslation('teamAssignments');
  const [expanded, setExpanded] = useState([]);
  const { groups, people, peopleById, substitutions } = model;
  const queryClient = useQueryClient();
  const [personDialog, setPersonDialog] = useState(null);
  const refreshBoard = () => {
    queryClient.invalidateQueries({ queryKey: ['team-board'] });
    queryClient.invalidateQueries({ queryKey: ['team-card'] });
  };
  const cancelCover = useMutation({
    mutationFn: cancelTeamSubstitution,
    onSuccess: () => {
      message.success(t('messages.saved'));
      refreshBoard();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });
  const fmtDay = (d) => dayjs(d).format('DD.MM.YYYY');
  // Colours follow the palette picked on the platform (Ant theme), not a fixed CSS var.
  const { token } = theme.useToken();

  const filters = QUICK_FILTERS.filter((f) => isManager || !f.manager);
  const quickOn = quick !== 'all';
  const [filtersOpen, setFiltersOpen] = useState(quickOn);

  // Opened from a company profile: only that firm, whatever the other filters say.
  const focused = focusCompany ? groups.find((g) => g.companyIds.includes(focusCompany)) : null;
  const mode = focused ? 'companies' : person ? 'person' : !isManager || list === 'companies' || q.trim() || quickOn ? 'companies' : 'team';

  useEffect(() => {
    if (!focused) return;
    setExpanded([focused.key]);
    setTimeout(() => document.querySelector(`[data-row-key="${focused.key}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 300);
  }, [focused?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const inPerson = (g) => !person || g.people.has(person);
  const visible = useMemo(() => {
    if (focused) return [focused];
    const qq = q.trim().toLowerCase();
    const { test } = filters.find((f) => f.key === quick) || filters[0];
    return groups.filter(
      (g) =>
        test(g) &&
        inPerson(g) &&
        (!fn || g.rows.some((a) => a.function === fn && (!person || a.employee === person))) &&
        (!qq ||
          g.name.toLowerCase().includes(qq) ||
          g.companies.some((c) => c.name.toLowerCase().includes(qq) || (c.fullName || '').toLowerCase().includes(qq)) ||
          (g.pib || '').includes(qq))
    );
  }, [groups, quick, q, fn, person, focused]); // eslint-disable-line react-hooks/exhaustive-deps

  const st = person ? personStats(groups, person, fn) : null;
  const allCompanies = useMemo(
    () => groups.flatMap((g) => g.companies.map((c) => ({ _id: c._id, name: c.name }))).sort((a, b) => a.name.localeCompare(b.name, 'sr')),
    [groups]
  );
  const { boxRef, tableWrapRef, boxHeight, bodyHeight } = useFitToWindow([mode, person, fn, filtersOpen, quick]);

  // In one person's view the counts are theirs; elsewhere the whole board's.
  const fnItems = [
    { key: '', label: t('fnPick.all') },
    ...FUNCTIONS.map((f) => ({
      key: f,
      label: t(`functions.${f}`),
      count: groups.filter((g) => g.rows.some((a) => a.function === f && (!person || a.employee === person))).length,
    })).filter((it) => !person || it.count > 0 || it.key === fn),
  ];
  const quickItems = filters
    .map((f) => ({ key: f.key, label: t(`quick.${f.key}`), warn: f.warn, count: groups.filter((g) => f.test(g) && inPerson(g)).length }))
    // A filter with nothing in it is left out; the chosen one always shows.
    .filter((it) => it.key === 'all' || it.key === quick || it.count > 0);

  const myConfirm = (g) => {
    const mine = g.rows.filter((a) => a.employee === me && !a.substitution);
    if (!mine.length) return null;
    if (mine.some((a) => a.confirmation === 'REPORTED' && !a.issueResolvedAt)) return 'REPORTED';
    return mine.every((a) => a.confirmation === 'CONFIRMED') ? 'CONFIRMED' : 'PENDING';
  };

  // ─── Columns ──────────────────────────────────────────────────────────────

  const companyColumn = {
    title: t('table.company'),
    key: 'name',
    width: mode === 'person' ? '30%' : fn ? '40%' : 240,
    fixed: mode === 'companies' && !fn ? 'left' : undefined,
    render: (_, g) => (
      <div className="min-w-0">
        <span className="font-medium">{g.name}</span>
        <Tooltip title={t('links.openCompany')}>
          <Link
            to={`${ROUTES.admin}/${ROUTES.companies}/${g.primaryId}`}
            state={teamBoardState()}
            onClick={(e) => {
              e.stopPropagation();
              rememberTeamBoard();
            }}
            className="ml-1 align-middle opacity-50 hover:opacity-100"
            aria-label={t('links.openCompany')}
          >
            <ExportOutlined />
          </Link>
        </Tooltip>
        {g.size > 1 && (
          <Tooltip title={g.companies.map((c) => c.name).join('\n')}>
            <Tag className="ml-1" bordered={false}>
              +{g.size - 1}
            </Tag>
          </Tooltip>
        )}
        {g.flags.map((f) => (
          <Tag key={f} className="ml-1" color={f === 'CLOSING' ? 'red' : 'default'}>
            {t(`flags.${f}`)}
          </Tag>
        ))}
        {g.clientStatus !== 'ACTIVE' && <Tag className="ml-1">{t(`clientStatuses.${g.clientStatus}`)}</Tag>}
        {g.isNew && (
          <Tag className="ml-1" color="green">
            {t('table.new')}
          </Tag>
        )}
        {/* Only what needs action; the client's notes live in its card. */}
        {g.noBookkeeper && <div className="text-xs text-orange-600">{t('table.noBookkeeper')}</div>}
        {g.unassigned && <div className="text-xs text-orange-600">{t('table.unassigned')}</div>}
        {isManager && g.newInGroup && <div className="text-xs text-orange-600">{t('table.newInGroup')}</div>}
        {g.transitionOverdue && <div className="text-xs text-red-600">{t('table.transitionOverdue')}</div>}
        {isManager && g.issues.length > 0 && <div className="text-xs text-orange-600">{t('table.issue')}</div>}
      </div>
    ),
  };

  const confirmColumn =
    !isManager && me && (mode !== 'person' || person === me)
      ? [
          {
            title: t('table.confirmation'),
            key: 'conf',
            width: 130,
            render: (_, g) => {
              const s = myConfirm(g);
              if (!s) return null;
              return <Tag color={s === 'CONFIRMED' ? 'green' : s === 'REPORTED' ? 'orange' : 'default'}>{t(`confirm.state_${s}`)}</Tag>;
            },
          },
        ]
      : [];

  const cols = fn ? [fn] : FUNCTIONS;
  const companiesColumns = [
    companyColumn,
    ...cols.map((f) => ({
      title: t(`functions.${f}`),
      key: f,
      width: fn ? undefined : f === 'BOOKKEEPING' ? 210 : f === 'REPORTS' || f === 'ADMIN' ? 140 : 170,
      render: (_, g) => {
        const entries = g.byFunction[f];
        const extra = !fn && f === 'PAYROLL' && g.headcount != null ? t('table.employeesShort', { count: g.headcount }) : null;
        return entries.length || extra ? (
          <div className="flex flex-wrap">
            {entries.map((e) => (
              <AssignmentChip key={`${e.employee}${e.role}${e.transition}`} entry={e} peopleById={peopleById} />
            ))}
            {extra && <div className="w-full text-xs opacity-60">{extra}</div>}
          </div>
        ) : (
          <span className="opacity-40">—</span>
        );
      },
    })),
    ...(fn === 'PAYROLL'
      ? [{ title: t('table.headcount'), key: 'hc', width: '22%', render: (_, g) => (g.headcount != null ? g.headcount : <span className="text-xs opacity-60">{g.headcountNote || '—'}</span>) }]
      : []),
    ...confirmColumn,
  ];

  // One person's own entries on an entity, and everybody else's.
  const ownEntries = (g) => FUNCTIONS.filter((f) => !fn || f === fn).flatMap((f) => g.byFunction[f].filter((e) => e.employee === person));
  const otherEntries = (g) => FUNCTIONS.flatMap((f) => g.byFunction[f].filter((e) => e.employee !== person));
  const personColumns = [
    companyColumn,
    {
      title: t('personView.work'),
      key: 'work',
      width: '30%',
      render: (_, g) => (
        <div className="flex flex-wrap">
          {ownEntries(g).map((e) => (
            <AssignmentChip key={`${e.function}${e.role}${e.transition}`} entry={e} peopleById={peopleById} asFunction />
          ))}
        </div>
      ),
    },
    {
      title: t('personView.others'),
      key: 'others',
      width: '28%',
      render: (_, g) => {
        const list = otherEntries(g);
        return list.length ? (
          <div className="flex flex-wrap">
            {list.map((e) => (
              <AssignmentChip key={`${e.function}${e.employee}${e.role}${e.transition}`} entry={e} peopleById={peopleById} showFunction />
            ))}
          </div>
        ) : (
          <span className="opacity-40">—</span>
        );
      },
    },
    // Headcount belongs to payroll only (Bora 18.10).
    ...(fn === 'PAYROLL'
      ? [
          {
            title: t('table.headcount'),
            key: 'hc',
            width: '12%',
            render: (_, g) => {
              const n = personHeadcountIn(g, person);
              return n != null ? <span className="tabular-nums">{n}</span> : <span className="opacity-40">—</span>;
            },
          },
        ]
      : []),
    ...confirmColumn,
  ];

  // ─── The team, one row per person ─────────────────────────────────────────

  const teamRows = useMemo(() => {
    const rows = people.map((p) => ({ key: p._id, p, st: personStats(groups, p._id, fn) }));
    return rows.sort(
      (a, b) =>
        (fn ? b.st.firmsOfFn - a.st.firmsOfFn : b.st.firms - a.st.firms) || personName(a.p).localeCompare(personName(b.p), 'sr')
    );
  }, [people, groups, fn]);
  const totals = {
    key: ALL_ROW,
    firms: fn ? groups.filter((g) => g.rows.some((a) => a.function === fn)).length : groups.length,
    byFn: Object.fromEntries(FUNCTIONS.map((f) => [f, groups.filter((g) => g.rows.some((a) => a.function === f)).length])),
    headcount: groups.reduce((s, g) => s + (g.headcount || 0), 0),
  };

  const personNotes = (p, st) => {
    const ab = absenceOf(substitutions, p._id);
    const notes = [];
    const [now] = ab.active;
    const [next] = ab.planned;
    const [covers] = ab.covering;
    if (now || next) {
      const x = now || next;
      notes.push(
        <span key="away" className={now ? 'text-cyan-700' : 'opacity-60'}>
          {t(now ? 'cover.awayShort' : 'cover.plannedShort', {
            from: dayjs(x.from).format('DD.MM.'),
            until: dayjs(x.until).format('DD.MM.'),
            name: personName(peopleById.get(x.substitute)),
          })}
        </span>
      );
    } else if (covers) {
      notes.push(
        <span key="covers" className="text-cyan-700">
          {t('cover.coveringFor', { until: dayjs(covers.until).format('DD.MM.'), name: personName(peopleById.get(covers.absent)) })}
        </span>
      );
    }
    if (isManager && st.firms > 0 && (!p.hasLogin || !p.canUsePortfolio)) {
      notes.push(
        <span key="login" className="text-orange-600">
          {!p.hasLogin ? t('people.noLogin') : t('people.noPortfolioRole')}
        </span>
      );
    }
    return notes;
  };

  const teamColumns = [
    {
      title: t('people.title'),
      key: 'person',
      width: 260,
      fixed: 'left',
      render: (_, r) => {
        if (r.key === ALL_ROW) return <b>{t('quick.all')}</b>;
        const total = r.st.rows || 1;
        const notes = personNotes(r.p, r.st);
        return (
          <div className="grid gap-1">
            <span className="font-medium">{personName(r.p)}</span>
            {r.st.rows > 0 && (
              <span className="flex h-1 max-w-[200px] overflow-hidden rounded bg-zinc-100" aria-hidden>
                {(fn ? [fn] : FUNCTIONS).map((f) =>
                  r.st.byFnRows[f] ? (
                    <span key={f} style={{ width: fn ? '100%' : `${(r.st.byFnRows[f] / total) * 100}%`, background: FUNCTION_BAR[f] }} />
                  ) : null
                )}
              </span>
            )}
            {notes.length > 0 && <span className="grid text-[11.5px]">{notes}</span>}
          </div>
        );
      },
    },
    {
      title: t('personView.firms'),
      key: 'firms',
      width: 80,
      align: 'right',
      render: (_, r) => {
        const n = r.key === ALL_ROW ? totals.firms : fn ? r.st.firmsOfFn : r.st.firms;
        return n ? <b className="tabular-nums">{n}</b> : <span className="opacity-40">—</span>;
      },
    },
    ...(fn ? [] : FUNCTIONS).map((f) => ({
      title: (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: FUNCTION_BAR[f] }} />
          {t(`functions.${f}`)}
        </span>
      ),
      key: f,
      width: 130,
      align: 'right',
      render: (_, r) => {
        const n = r.key === ALL_ROW ? totals.byFn[f] : r.st.byFn[f];
        return n ? <span className="tabular-nums">{n}</span> : <span className="opacity-30">—</span>;
      },
    })),
    ...(fn === 'PAYROLL'
      ? [
          {
            title: <span className="whitespace-nowrap">{t('table.headcount')}</span>,
            key: 'hc',
            width: 190,
            align: 'right',
            render: (_, r) => {
              const n = r.key === ALL_ROW ? totals.headcount : r.st.headcount;
              return n ? <span className="tabular-nums">{n}</span> : <span className="opacity-30">—</span>;
            },
          },
        ]
      : []),
    {
      title: t('personView.transitions'),
      key: 'trans',
      width: 150,
      render: (_, r) =>
        r.key === ALL_ROW ? null : (
          <span className="grid text-xs">
            {r.st.out > 0 && <span style={{ color: token.colorWarningText }}>{t('people.handsOver', { count: r.st.out })}</span>}
            {r.st.in > 0 && <span style={{ color: token.colorSuccessText }}>{t('people.takesOver', { count: r.st.in })}</span>}
          </span>
        ),
    },
  ];

  // ─── Layout ───────────────────────────────────────────────────────────────

  const tableProps =
    mode === 'team'
      ? {
          rowKey: 'key',
          dataSource: [totals, ...teamRows],
          columns: teamColumns,
          scroll: { x: fn ? 700 : 1460, ...(bodyHeight ? { y: bodyHeight } : {}) },
          onRow: (r) => ({
            onClick: () => (r.key === ALL_ROW ? setList('companies') : setPerson(r.key)),
            style: { cursor: 'pointer' },
          }),
          rowClassName: (r) => (r.key === ALL_ROW ? 'team-board-total' : r.st.firms ? '' : 'opacity-50'),
        }
      : {
          rowKey: 'key',
          dataSource: visible,
          columns: mode === 'person' ? personColumns : companiesColumns,
          tableLayout: mode === 'person' || fn ? 'fixed' : undefined,
          scroll: { x: mode === 'person' ? 900 : fn ? 640 : 1240, ...(bodyHeight ? { y: bodyHeight } : {}) },
          expandable: {
            expandedRowKeys: expanded,
            onExpandedRowsChange: (keys) => setExpanded(keys),
            expandRowByClick: true,
            expandedRowRender: (g) => <TeamFirmCard companyId={g.primaryId} embedded />,
          },
        };

  const picked = person ? peopleById.get(person) : null;
  // One person's view has no card of its own: name, numbers and actions share the row with the filters.
  const personHead = mode === 'person' && picked && st;
  const personAbsences = personHead
    ? (({ active, planned, covering }) => [...active, ...planned, ...covering])(absenceOf(substitutions, person))
    : [];

  return (
    <div ref={boxRef} className={`flex min-w-0 flex-col gap-3 ${boxHeight ? 'min-h-0' : ''}`} style={boxHeight ? { height: boxHeight } : undefined}>
      <style>
        {[
          // The frame carries the platform colour; Ant's own outer border would double it.
          '.team-board-table .ant-table, .team-board-table .ant-table-container { border: 0 !important; border-radius: 0 !important; }',
          `.team-board-table { border: 2px solid ${token.colorPrimary}; }`,
          '.team-board-table table { border-radius: 0 !important; }',
          `.team-board-table .ant-table-thead > tr > th { background: ${token.colorPrimaryBg} !important; font-weight: 600; border-bottom: 1px solid ${token.colorPrimaryBorder} !important; }`,
          '.team-board-table .ant-table-thead > tr > th::before { display: none; }',
          '.team-board-table .ant-table-cell:last-child, .team-board-table .ant-table-cell-scrollbar { border-inline-end: 0 !important; }',
          '.team-board-table .ant-table-tbody > tr:last-child > td { border-bottom: 0; }',
          `.team-board-table .team-board-total > td { background: ${token.colorFillQuaternary}; }`,
        ].join('\n')}
      </style>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {isManager && mode !== 'team' && (
          <Button shape="round" size="small" icon={<ArrowLeftOutlined />} onClick={goOverview}>
            {t('personView.backToTeam')}
          </Button>
        )}
        {personHead && (
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <h3 className="m-0 flex items-center gap-2 text-base font-semibold">
              {personName(picked)}
              {isManager && (
                <Tooltip title={t('links.openPerson')}>
                  <Link
                    to={
                      picked.userId
                        ? `${ROUTES.admin}/${ROUTES.users}/${OPERATION.view}/${picked.userId}`
                        : `${ROUTES.admin}/${ROUTES.employees}/${person}`
                    }
                    state={teamBoardState()}
                    onClick={rememberTeamBoard}
                    className="text-sm font-normal opacity-60 hover:opacity-100"
                    aria-label={t('links.openPerson')}
                  >
                    <ExportOutlined />
                  </Link>
                </Tooltip>
              )}
            </h3>
            <span className="text-xs opacity-70">
              {[
                t('people.firms', { count: st.firms }),
                ...FUNCTIONS.filter((f) => st.byFn[f]).map((f) => `${t(`functions.${f}`)}: ${st.byFn[f]}`),
                st.headcount ? t('people.employees', { count: st.headcount }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              {st.out > 0 && <span style={{ color: token.colorWarningText }}> · {t('people.handsOver', { count: st.out })}</span>}
              {st.in > 0 && <span style={{ color: token.colorSuccessText }}> · {t('people.takesOver', { count: st.in })}</span>}
            </span>
          </div>
        )}
        <PillGroup items={fnItems} value={fn} onChange={(v) => { setFn(v); setExpanded([]); }} label={t('fnPick.label')} />
        <div className="ml-auto flex items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: token.colorPrimary }} />}
            placeholder={t('table.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('table.search')}
            style={{ width: 240, height: 32, borderRadius: 999, borderColor: token.colorPrimaryBorder }}
          />
          <Badge dot={quickOn} color={token.colorWarning} offset={[-4, 4]}>
            <Button shape="round" icon={<FilterOutlined />} type={filtersOpen ? 'primary' : 'default'} ghost={filtersOpen} onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen}>
              {t('filters.button')}
            </Button>
          </Badge>
          {mode !== 'team' && <span className="whitespace-nowrap text-xs opacity-60">{t('table.countOf', { shown: visible.length, total: groups.length })}</span>}
        </div>
        {personHead && isManager && st.firms > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button size="small" shape="round" onClick={() => setPersonDialog('cover')}>
              {t('cover.open')}
            </Button>
            <Button size="small" shape="round" onClick={() => setPersonDialog('bulk')}>
              {t('bulk.open')}
            </Button>
          </div>
        )}
      </div>

      {personAbsences.length > 0 && (
        <ul className="m-0 grid list-none gap-1 p-0 text-xs">
          {personAbsences.map((x) => (
            <li key={x._id} className="flex flex-wrap items-center gap-2">
              <Tag color={x.status === 'ACTIVE' ? 'cyan' : 'default'} className="!m-0">
                {x.absent === person
                  ? t(x.status === 'ACTIVE' ? 'cover.awayNow' : 'cover.awayPlanned', {
                      from: fmtDay(x.from),
                      until: fmtDay(x.until),
                      name: personName(peopleById.get(x.substitute)),
                    })
                  : t('cover.coveringFor', { until: fmtDay(x.until), name: personName(peopleById.get(x.absent)) })}
              </Tag>
              {isManager && (
                <Button
                  size="small"
                  shape="round"
                  type="text"
                  danger
                  onClick={() =>
                    Modal.confirm({
                      title: t('cover.cancelConfirm'),
                      okText: t('cover.cancel'),
                      cancelText: t('actions.cancel'),
                      okButtonProps: { shape: 'round', danger: true },
                      cancelButtonProps: { shape: 'round' },
                      onOk: () => cancelCover.mutateAsync(x._id),
                    })
                  }
                >
                  {t('cover.cancel')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {personHead && (
        <>
          <SubstitutionDialog
            open={personDialog === 'cover'}
            absent={picked}
            people={people}
            byFn={st.byFn}
            onClose={() => setPersonDialog(null)}
            onDone={refreshBoard}
          />
          <BulkTransferDialog
            open={personDialog === 'bulk'}
            from={picked}
            people={people}
            byFn={st.byFn}
            firms={st.firms}
            onClose={() => setPersonDialog(null)}
            onDone={refreshBoard}
          />
        </>
      )}

      {filtersOpen && (
        <div className="grid gap-2 rounded-xl border border-dashed px-3 py-2" style={{ borderColor: token.colorPrimaryBorder }}>
          <PillGroup items={quickItems} value={quick} onChange={setQuick} label={t('quick.label')} />
          {isManager && (
            <label className="flex flex-wrap items-center gap-2 text-sm">
              <span className="opacity-70">{t('clientStatus.show')}</span>
              <Select
                mode="multiple"
                size="small"
                style={{ minWidth: 240 }}
                value={clientStatus}
                onChange={(v) => setClientStatus(v.length ? v : ['ACTIVE'])}
                // HLB TM itself is not a client; it has no team to show.
                options={CLIENT_STATUSES.filter((s) => s !== 'OWN').map((s) => ({ value: s, label: t(`clientStatuses.${s}`) }))}
                aria-label={t('clientStatus.filter')}
              />
            </label>
          )}
        </div>
      )}

      {fn && mode === 'companies' && (
        <div className="-mt-1 text-xs opacity-70">
          {t('fnPick.summary', {
            fn: t(`functions.${fn}`),
            firms: visible.length,
            people: new Set(visible.flatMap((g) => g.rows.filter((a) => a.function === fn).map((a) => a.employee))).size,
          })}
          {fn === 'PAYROLL' ? ` · ${t('people.employees', { count: visible.reduce((s, g) => s + (g.headcount || 0), 0) })}` : ''}
        </div>
      )}
      {focused && (
        <div className="flex items-center gap-2">
          <Tag color={token.colorPrimary} className="!m-0 !rounded-full !px-3 !py-0.5 !text-[13px]">
            {t('focus.only', { name: focused.name })}
          </Tag>
          <Button shape="round" size="small" onClick={onFocused}>
            {t('focus.showAll')}
          </Button>
        </div>
      )}

      {/* General admin tasks: on the "Admin tasks" view, and with one person's work. */}
      {(fn === 'ADMIN' || mode === 'person') && !focused && (
        <GeneralTasks
          people={people}
          companies={allCompanies}
          person={person}
          isManager={isManager}
          hideWhenEmpty={fn !== 'ADMIN'}
        />
      )}

      {/* Not fitted: clip, not hidden, which would become the scroll parent of the sticky header. */}
      <div ref={tableWrapRef} className={`team-board-table rounded-xl ${boxHeight ? 'min-h-0 flex-1 overflow-hidden' : 'overflow-clip'}`}>
        <Table
          size="small"
          bordered
          // The whole list scrolls inside the frame; paging would only add a second scroll.
          pagination={false}
          sticky={bodyHeight ? false : { getContainer: () => boxRef.current?.closest(FRAME_SELECTOR) || window }}
          locale={{ emptyText: t('table.empty') }}
          {...tableProps}
        />
      </div>
    </div>
  );
}
